import React from "react";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { Col, Grid, NarrowContainer } from "components/Containers";
import { breakpoints, colors } from "style";
import { H1 } from "components/Text";
import { MarketingButton } from "components/Buttons";
import { Link } from "components/Links";
import MemberTestimonialsCarousel from "./MemberTestimonialsCarousel";
import Abhinav from "assets/images/members/abhinav_headshot.jpg";
import Siamak from "assets/images/members/siamak_headshot.jpg";
import Nadia from "assets/images/members/nadia_headshot.jpg";
import Zawar from "assets/images/members/zawar_headshot.jpg";
import Nicolas from "assets/images/members/nicholas_headshot.jpg";
import Laleet from "assets/images/members/laleet_headshot.jpg";
import Faith from "assets/images/members/faith_headshot.jpg";
import Mateus from "assets/images/members/mateus_headshot.jpg";
import Xavier from "assets/images/members/xavier_headshot.jpg";
import Tommy from "assets/images/members/tommy_headshot.jpg";
import Valeriu from "assets/images/members/valeriu_headshot.jpg";
import { MARKETING_WEBSITE_URL } from "constants/index";

const MEMBER_IMAGES = {
  abhinav: Abhinav,
  siamak: Siamak,
  nadia: Nadia,
  zawar: Zawar,
  nicolas: Nicolas,
  laleet: Laleet,
  faith: Faith,
  mateus: Mateus,
  xavier: Xavier,
  tommy: Tommy,
  valeriu: Valeriu,
};

/**
 * MissionRolesPublicTestimonials
 */
const MissionRolesPublicTestimonials = () => (
  <div css={styles.container} id="testimonials">
    <NarrowContainer>
      <div css={styles.inner_container}>
        <Grid.col start={4} end={10}>
          <Col gap="0" css={styles.title_container}>
            <H1 isPublic css={styles.title}>
              <FormattedMessage id="Missions.Roles.Public.MissionRolesPublicTestimonials.Title" />
            </H1>
          </Col>
        </Grid.col>
        <MemberTestimonialsCarousel testimonials={TESTIMONIAL_DATA} />
        <Col gap="1.5rem" css={styles.cta_container}>
          <Link to={`${MARKETING_WEBSITE_URL}/join-the-network/member-stories`} css={styles.cta_link} target="_blank" rel="noopener noreferrer">
            <MarketingButton>
              <FormattedMessage id="Components.BlogPost.Testimonials.Members.CTA" />
            </MarketingButton>
          </Link>
          <p css={styles.cta_description}>
            <FormattedMessage id="Components.BlogPost.Testimonials.Members.Description" />
          </p>
        </Col>
      </div>
    </NarrowContainer>
  </div>
);

const TESTIMONIAL_DATA = [
  {
    id: 1,
    image: MEMBER_IMAGES.abhinav,
    name: "Abhinav P.",
    designation: "Product Manager & Team Lead",
    message: "The team at Mission.dev really understood my skills and helped me land a role I was truly excited about, and I'm incredibly grateful for that.",
  },
  {
    id: 2,
    image: MEMBER_IMAGES.siamak,
    name: "Siamak S.",
    designation: "Team Lead",
    message: "Mission.dev offers a rare opportunity for developers and product leads to work on real products with true end-to-end ownership.",
  },
  {
    id: 3,
    image: MEMBER_IMAGES.nadia,
    name: "Nadia",
    designation: "SQA Engineer",
    message: "Working with Mission has been a rewarding experience, with a collaborative culture and strong support from the team. I'm grateful for the opportunity to have contributed and grown professionally during my time here.",
  },
  {
    id: 4,
    image: MEMBER_IMAGES.zawar,
    name: "Zawar N.",
    designation: "Senior Software Engineer",
    message: "I'm proud of the impact I was able to make and deeply grateful for the opportunities to grow, learn, and collaborate with such talented individuals at Mission.dev",
  },
  {
    id: 5,
    image: MEMBER_IMAGES.nicolas,
    name: "Nicolas M.",
    designation: "Tech Lead",
    message: "Working with Mission.dev has connected me to exciting projects and an incredible network of talented professionals",
  },
  {
    id: 6,
    image: MEMBER_IMAGES.laleet,
    name: "Laleet A.",
    designation: "Senior Full Stack Engineer",
    message: "Freelancing with Mission.dev makes it possible to do meaningful work across time zones without sacrificing balance.",
  },
  {
    id: 7,
    image: MEMBER_IMAGES.faith,
    name: "Faith A.",
    designation: "Senior Software Developer",
    message: "The onboarding and communication were smooth, supportive, and Mission.dev followed through on every commitment.",
  },
  {
    id: 8,
    image: MEMBER_IMAGES.mateus,
    name: "Mateus S.",
    designation: "Android Developer",
    message: "Mission.dev offers meaningful projects and stability, while supporting the personal balance that matters most.",
  },
  {
    id: 9,
    image: MEMBER_IMAGES.xavier,
    name: "Xavier J.",
    designation: "React Native Expert - Tech Lead",
    message: "Working with Mission.dev has been one of the most beneficial decisions in my career, giving me the freedom to choose projects, work efficiently, and grow within a supportive network of software engineers.",
  },
  {
    id: 10,
    image: MEMBER_IMAGES.tommy,
    name: "Tommy L.",
    designation: "Vue JS Expert - Tech Lead",
    message: "The main reason I love working with Mission is the great people I get to collaborate with. I know I can always rely on and learn from everyone.",
  },
  {
    id: 11,
    image: MEMBER_IMAGES.valeriu,
    name: "Valeriu C.",
    designation: "Ruby on Rails Expert - Tech Lead",
    message: "The most amazing thing about being part of Mission.dev is having the chance to work on multiple projects across different domains in less than a year, something I never experienced before.",
  },
];

const styles = {
  container: css`
    color: ${colors.grayAnatomyDark};
    background: ${colors.white};
    padding-bottom: 12rem;

    @media (max-width: ${breakpoints.mobile}) {
      padding: 3rem 0;
    }
  `,
  inner_container: css`
    background: ${colors.grayAnatomyLight8};
    border-radius: 3.5rem;
    padding: 8rem 4rem;

    @media (max-width: ${breakpoints.mobile}) {
      padding: 3rem 1rem;
      border-radius: 2rem;
    }
  `,
  title_container: css`
    display: flex;
    align-items: center; 
    justify-content: center;
    width: 100%;
    margin-bottom: 4rem;
    text-align: center;

    @media (max-width: ${breakpoints.mobile}) {
      margin-bottom: 2rem;
    }
  `,
  title: css`
    margin-bottom: 2rem;
  `,
  cta_container: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 4rem;
    text-align: center;

    @media (max-width: ${breakpoints.mobile}) {
      margin-top: 3rem;
    }
  `,
  cta_link: css`
    text-decoration: none;
  `,
  cta_description: css`
    color: ${colors.grayAnatomyMedium};
    font-size: 1.6rem;
    line-height: 1.5;
    margin: 0;

    @media (max-width: ${breakpoints.mobile}) {
      font-size: 1.4rem;
    }
  `,
};

export default MissionRolesPublicTestimonials;
